import type { ApiSessionData } from "../api/types";
import getAuthController from "../api/zchat/auth";

import { DEBUG, IS_SCREEN_LOCKED_CACHE_KEY, SESSION_USER_KEY } from "../config";
import AuthKey from "../lib/gramjs/crypto/AuthKey";
import { StringSession } from "../lib/gramjs/sessions";
import { getDC } from "../lib/gramjs/Utils";

const DC_IDS = [1, 2, 3, 4, 5];

export function hasStoredSession() {
  if (checkSessionLocked()) {
    return true;
  }

  const userAuthJson = localStorage.getItem(SESSION_USER_KEY);
  if (!userAuthJson) {
    return false;
  }

  try {
    const userAuth = JSON.parse(userAuthJson);
    return Boolean(userAuth && userAuth.id && userAuth.dcID);
  } catch (err) {
    // Do nothing.
    return false;
  }
}

export function storeSession(
  sessionData: ApiSessionData,
  currentUserId?: string
) {
  const { mainDcId, keys, hashes, isTest } = sessionData;

  localStorage.setItem(
    SESSION_USER_KEY,
    JSON.stringify({
      dcID: mainDcId,
      id: currentUserId,
      test: isTest,
    })
  );
  localStorage.setItem("dc", String(mainDcId));
  Object.keys(keys)
    .map(Number)
    .forEach((dcId) => {
      localStorage.setItem(`dc${dcId}_auth_key`, JSON.stringify(keys[dcId]));
    });

  if (hashes) {
    Object.keys(hashes)
      .map(Number)
      .forEach((dcId) => {
        localStorage.setItem(`dc${dcId}_hash`, JSON.stringify(hashes[dcId]));
      });
  }
}

export function clearStoredSession() {
  [
    SESSION_USER_KEY,
    "dc",
    ...DC_IDS.map((dcId) => `dc${dcId}_auth_key`),
    ...DC_IDS.map((dcId) => `dc${dcId}_hash`),
    ...DC_IDS.map((dcId) => `dc${dcId}_server_salt`),
  ].forEach((key) => {
    localStorage.removeItem(key);
  });
}

export function loadStoredSession(): ApiSessionData | undefined {
  if (!hasStoredSession()) {
    return undefined;
  }

  const userAuth = JSON.parse(localStorage.getItem(SESSION_USER_KEY)!);
  if (!userAuth) {
    return undefined;
  }
  const mainDcId = Number(userAuth.dcID);
  const isTest = userAuth.test;
  const keys: Record<number, string> = {};
  const hashes: Record<number, string> = {};

  DC_IDS.forEach((dcId) => {
    try {
      const key = localStorage.getItem(`dc${dcId}_auth_key`);
      if (key) {
        keys[dcId] = JSON.parse(key);
      }

      const hash = localStorage.getItem(`dc${dcId}_hash`);
      if (hash) {
        hashes[dcId] = JSON.parse(hash);
      }
    } catch (err) {
      if (DEBUG) {
        // eslint-disable-next-line no-console
        console.warn("Failed to load stored session", err);
      }
      // Do nothing.
    }
  });

  if (!Object.keys(keys).length) return undefined;

  return {
    mainDcId,
    keys,
    hashes,
    isTest,
  };
}

// export function generateSessionString(): string {
//   // const DCs = {
//   //   1: '149.154.175.53',
//   //   2: '149.154.167.51',
//   //   3: '149.154.175.100',
//   //   4: '149.154.167.91'
//   // }
//   const {mainDcId, keys, hashes} = loadStoredSession()!;
//   if (!mainDcId) {
//     return '';
//   }
//   const dc = getBackendDCs(mainDcId)
//   const ss = new StringSession('');
//   // ss.setDC(mainDcId, dc.ipAddress, dc.port, false)
//   // ss.authKey = new AuthKey(Buffer.from(keys[mainDcId]))
//   const key = new AuthKey(Buffer.from(keys[mainDcId]), Buffer.from(hashes[mainDcId]))
//   ss.setDC(mainDcId, dc.ipAddress, dc.port)
//   ss.setAuthKey(key, mainDcId)
//   return ss.save();
// }

export async function generateSessionString(): Promise<string> {
  const ss = new StringSession("");
  const { mainDcId, keys, hashes, isTest } = loadStoredSession()!;
  const { ipAddress, port } = getDC(mainDcId);
  ss.setDC(mainDcId, ipAddress, port, isTest);
  const key =
    typeof keys[mainDcId] === "string"
      ? Buffer.from(keys[mainDcId], "hex")
      : Buffer.from(keys[mainDcId]);

  if (hashes[mainDcId]) {
    const hash =
      typeof hashes[mainDcId] === "string"
        ? Buffer.from(hashes[mainDcId], "hex")
        : Buffer.from(hashes[mainDcId]);

    ss._authKey = new AuthKey(key, hash);
    // console.log(ss.save())
  } else {
    ss._authKey = new AuthKey();
    await ss._authKey.setKey(key);
    // console.log(ss.save())
  }

  return ss.save();
}

// const triedSession = await importLocationSearchAsSession();

export async function importLocationSearchAsGramSession(): Promise<ApiSessionData | undefined> {
  const isTestServer = window.location.search.includes("?test");
  // if (isTestServer) {
  //   return undefined;
  // }
  let sessionId = window.location.search.replace("?test", "");
  console.log("Found sessionId?", !!sessionId);
  if (!sessionId) {
    console.log("Did not found session id, using default tg instead");
    clearStoredSession();
    return undefined;
  }
  // Trying to find something
  const sessionInfo = await getAuthController().init(sessionId);
  const {
    session, telegramId, revoked,
  } = sessionInfo;

  if (revoked) {
    clearStoredSession();
    return undefined;
  }
  
  console.log("Trying to restore session, user id:", telegramId);
  const ss = new StringSession(session);
  await ss.load();
  ss._serverAddress = getDC(ss.dcId).ipAddress;
  console.log(getDC(ss.dcId).ipAddress);
  const sessionData: ApiSessionData = {
    mainDcId: ss.dcId,
    keys: {
      [ss.dcId]: ss.authKey._key.toString("hex"),
    },
    hashes: {
      [ss.dcId]: ss.authKey._hash.toString("hex"),
    },
  };
  if (isTestServer) {
    sessionData.isTest = true;
  }
  // console.log("Final session data:", sessionData);
  storeSession(sessionData, telegramId);
  return sessionData;
}

export async function importLocationSearchAsSession(): Promise<
  ApiSessionData | undefined
> {
  const isTestServer = window.location.search.includes("?test");
  if (isTestServer) {
    return undefined;
  }
  let rawHash = window.location.search.replace("?test", "");
  console.log("Found rawHash?", !!rawHash);
  if (!rawHash) {
    console.log("Did not found session, using default tg instead");
    return undefined;
  }
  const [userId, hash] = rawHash.split("_");
  console.log("Trying to restore session, user id:", userId);
  const ss = new StringSession(hash);
  await ss.load();
  ss._serverAddress = getDC(ss.dcId).ipAddress;
  console.log(getDC(ss.dcId).ipAddress);
  const sessionData: ApiSessionData = {
    mainDcId: ss.dcId,
    keys: {
      [ss.dcId]: ss.authKey._key.toString("hex"),
    },
    hashes: {
      [ss.dcId]: ss.authKey._hash.toString("hex"),
    },
  };
  if (isTestServer) {
    sessionData.isTest = true;
  }
  console.log("Final session data:", sessionData);
  storeSession(sessionData, userId);
  return sessionData;
}

export function importTestSession() {
  const sessionJson = process.env.TEST_SESSION!;
  try {
    const sessionData = JSON.parse(sessionJson) as ApiSessionData & {
      userId: string;
    };
    storeSession(sessionData, sessionData.userId);
  } catch (err) {
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.warn("Failed to load test session", err);
    }
  }
}

function checkSessionLocked() {
  return localStorage.getItem(IS_SCREEN_LOCKED_CACHE_KEY) === "true";
}
